import { createFileRoute } from '@tanstack/react-router';
import {
  Link,
  useNavigate,
  useTranslation,
  useSelector,
  useDispatch,
  Turnstile,
  type TurnstileInstance,
} from '@spcc/core';
import { getNextLanguage, isRtlLanguage } from '@spcc/portal-shared';
import { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Label,
  Eye,
  EyeOff,
  LogIn,
  Globe,
  Alert,
  AlertDescription,
  AlertTriangle,
  Checkbox,
} from '@spcc/uikit';

import {
  authActions,
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectIsAttemptBlocked,
  selectLoginAttemptsRemaining,
} from '@spcc/domains/auth';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsAuthLoading);
  const isAttemptBlocked = useSelector(selectIsAttemptBlocked);
  const loginAttemptsRemaining = useSelector(selectLoginAttemptsRemaining);

  // Local state
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

  const isRTL = isRtlLanguage(i18n.language);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      dispatch(authActions.clearError());
    };
  }, [dispatch]);

  const toggleLanguage = () => {
    const newLang = getNextLanguage(i18n.language);
    i18n.changeLanguage(newLang);
    document.documentElement.dir = isRtlLanguage(newLang) ? 'rtl' : 'ltr';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(
      authActions.login({
        identifier: email,
        password,
        rememberMe,
        turnstileToken: turnstileToken ?? undefined,
      }),
    );
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  };

  return (
    <div
      className="safe-top relative min-h-screen flex items-center justify-center overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background gradients */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      {/* Language toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-[calc(env(safe-area-inset-top)+1rem)] end-4 glass"
        onClick={toggleLanguage}
      >
        <Globe className="h-5 w-5" />
      </Button>

      <div className="w-full max-w-md px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/spcc-logo.png"
              alt="Southern Province Cement Company"
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('auth.loginSubtitle')}
          </p>
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">{t('auth.signIn')}</CardTitle>
            <CardDescription>{t('auth.enterCredentials')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-testid="login-form"
            >
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.emailOrPhone')}</Label>
                <Input
                  id="email"
                  type="text"
                  placeholder={t('auth.emailOrPhonePlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-background/50"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:underline"
                    data-testid="login-forgot-password"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-background/50 pr-10"
                    data-testid="login-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center space-x-2 rtl:space-x-reverse kiosk-hide">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(Boolean(v))}
                />
                <Label
                  htmlFor="remember-me"
                  className="text-sm font-normal cursor-pointer"
                >
                  {t('auth.rememberMe', 'Remember me')}
                </Label>
              </div>

              {/* Attempts warning (final attempt only) */}
              {loginAttemptsRemaining === 1 && !isAttemptBlocked && (
                <Alert
                  variant="destructive"
                  className="border-amber-300 bg-amber-50 text-amber-800 [&>svg]:text-amber-600"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {t('attemptsRemaining', {
                      defaultValue:
                        'Last attempt before your account is locked.',
                    })}
                  </AlertDescription>
                </Alert>
              )}

              {/* Account locked */}
              {isAttemptBlocked && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {t(
                      'accountLocked',
                      'Your account has been locked due to too many failed login attempts. Please contact support or reset your password.',
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {TURNSTILE_SITE_KEY && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                  options={{ theme: 'auto', size: 'flexible' }}
                />
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || isAttemptBlocked || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    {t('auth.signIn')}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">
                {t('auth.noAccount')}{' '}
              </span>
              <Link
                to="/register"
                className="text-primary hover:underline"
                data-testid="login-register"
              >
                {t('auth.signUp')}
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {t('auth.termsNotice')}{' '}
          <Link to="/privacy-policy" className="text-primary hover:underline">
            {t('auth.privacyPolicy')}
          </Link>
        </p>
      </div>
    </div>
  );
}
